import styled from 'styled-components'
import { BREAKPOINTS, TAP_MIN } from "../../GlobalTheme"

// ---- Tab navigation (mirrors Charger.tsx MenuContainer / MenuElement) ----
export const TabMenuContainer = styled.div`
  position: absolute;
  top: -1.5vh;
  display: flex;
  align-items: center;

  @media (max-width: ${BREAKPOINTS.shell}px) {
    /* Sticks to the top of the page scroller. Scrolled normally the strip slid up under the fixed
       header and, part-way, showed nothing but the active tab's underline floating in an empty
       band. Keeping it pinned also keeps the tabs reachable while reading down the page. */
    position: sticky;
    top: 0px;
    z-index: 2;
    background-color: ${props => props.theme.colors.background};
    max-width: 100%;
    overflow-x: auto;
    overscroll-behavior-x: contain;
    padding-bottom: 6px;

    p { white-space: nowrap; }
  }
`

export const TabMenuElement = styled.p<{ $selected: boolean }>`
  font-weight: 600;
  font-size: 12px;
  color: ${props => (props.$selected ? props.theme.colors.gray_900 : props.theme.colors.gray_700)};
  margin: 0px;
  margin-left: 10px;
  margin-right: 10px;
  padding-bottom: 2px;
  border-bottom: ${props => (props.$selected ? `2px solid ${props.theme.colors.primary}` : '0px solid transparent')};
  cursor: pointer;

  @media (max-width: ${BREAKPOINTS.shell}px) {
    min-height: ${TAP_MIN}px;
    display: flex;
    align-items: center;
  }
`

export const Breadcrumb = styled.p`
  position: absolute;
  top: -7vh;
  left: 1vw;
  font-weight: 600;
  font-size: 12px;
  color: ${props => props.theme.colors.gray_700};
  margin: 0px;
`

// Content wrapper: clears the absolutely-positioned tab bar and scrolls.
export const TabContent = styled.div`
  margin-top: 24px;
  height: calc(90vh - 40px);
  @media (max-width: ${BREAKPOINTS.shell}px) {
    height: auto;
    max-height: calc(100dvh - 160px);
    overscroll-behavior: contain;
  }
  overflow-y: auto;
  padding-right: 4px;
`

// ---- Cards ----
export const Card = styled.div`
  background-color: ${props => props.theme.colors.white};
  border-radius: 24px;
  padding: 24px;
  position: relative;
  margin-bottom: 16px;
  break-inside: avoid;
`

export const CardTitle = styled.p`
  font-weight: 700;
  font-size: 16px;
  color: ${props => props.theme.colors.gray_900};
  margin: 0px 0px 20px 0px;
`

// ---- Header card (avatar + name + grid) ----
export const HeaderRow = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  margin-bottom: 24px;
`

export const AvatarTile = styled.div`
  width: 56px;
  height: 56px;
  border-radius: 16px;
  background-color: ${props => props.theme.colors.info};
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  flex-shrink: 0;
`

export const AvatarImg = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`

export const UserNameLabel = styled.p`
  font-weight: 600;
  font-size: 12px;
  color: ${props => props.theme.colors.gray_500};
  margin: 0px;
`

export const UserName = styled.p`
  font-weight: 700;
  font-size: 26px;
  color: ${props => props.theme.colors.gray_900};
  margin: 2px 0px 0px 0px;
`

// ---- Responsive detail grid (6 / 3 / 2 columns) ----
export const DetailGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(6, minmax(0, 1fr));
  gap: 20px 24px;

  @media (max-width: 1200px) {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }
  @media (max-width: 700px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
`

// Header grid: UID + email get ~1.7x the width of the short date/language columns.
export const HeaderGrid = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1.7fr) minmax(0, 1.7fr) minmax(0, 1fr) minmax(0, 1fr) minmax(0, 1fr) minmax(0, 1fr);
  gap: 20px 24px;

  @media (max-width: 1200px) {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }
  @media (max-width: 700px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
`

export const DetailLabel = styled.p`
  font-weight: 700;
  font-size: 11px;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: ${props => props.theme.colors.gray_500};
  margin: 0px 0px 6px 0px;
`

export const DetailValue = styled.div`
  font-weight: 600;
  font-size: 15px;
  color: ${props => props.theme.colors.gray_900};
  display: flex;
  align-items: center;
  gap: 6px;
  min-width: 0;
  word-break: break-word;
`

export const MutedValue = styled.span`
  font-weight: 500;
  font-style: italic;
  color: ${props => props.theme.colors.gray_500};
`

export const ExternalLink = styled.a`
  color: ${props => props.theme.colors.primaryActive};
  text-decoration: none;
  cursor: pointer;
  &:hover { text-decoration: underline; }
`

// ---- Badge (pill) ----
type Tone = 'success' | 'neutral' | 'primary' | 'danger' | 'warning'
export const Badge = styled.span<{ $tone?: Tone }>`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 3px 10px;
  border-radius: 8px;
  font-weight: 600;
  font-size: 12px;
  white-space: nowrap;
  color: ${props => {
    switch (props.$tone) {
      case 'success': return props.theme.colors.successActive
      case 'primary': return props.theme.colors.primaryActive
      case 'danger': return props.theme.colors.dangerActive
      case 'warning': return props.theme.colors.warningActive
      default: return props.theme.colors.gray_700
    }
  }};
  background-color: ${props => {
    switch (props.$tone) {
      case 'success': return props.theme.colors.successLight
      case 'primary': return props.theme.colors.primaryLight
      case 'danger': return props.theme.colors.dangerLight
      case 'warning': return props.theme.colors.warningLight
      default: return props.theme.colors.gray_100
    }
  }};
`

// ---- KPI stat tiles (list page) ----
export const StatBar = styled.div`
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 16px;
  margin-bottom: 16px;

  @media (max-width: 900px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
`

export const StatTile = styled.div<{ $clickable?: boolean; $active?: boolean; $tone?: Tone }>`
  background-color: ${props => props.theme.colors.white};
  border-radius: 16px;
  padding: 18px 20px;
  display: flex;
  align-items: center;
  gap: 14px;
  cursor: ${props => (props.$clickable ? 'pointer' : 'default')};
  transition: box-shadow 0.12s ease;
  /* Active filter: an inset ring in the tile's own tone (inset avoids any layout shift). */
  box-shadow: ${props => {
    if (!props.$active) return 'none'
    switch (props.$tone) {
      case 'success': return `inset 0px 0px 0px 2px ${props.theme.colors.successActive}`
      case 'danger': return `inset 0px 0px 0px 2px ${props.theme.colors.dangerActive}`
      case 'warning': return `inset 0px 0px 0px 2px ${props.theme.colors.warningActive}`
      default: return `inset 0px 0px 0px 2px ${props.theme.colors.primaryActive}`
    }
  }};
  &:hover {
    box-shadow: ${props => {
      if (props.$active) {
        switch (props.$tone) {
          case 'success': return `inset 0px 0px 0px 2px ${props.theme.colors.successActive}`
          case 'danger': return `inset 0px 0px 0px 2px ${props.theme.colors.dangerActive}`
          case 'warning': return `inset 0px 0px 0px 2px ${props.theme.colors.warningActive}`
          default: return `inset 0px 0px 0px 2px ${props.theme.colors.primaryActive}`
        }
      }
      return props.$clickable ? `inset 0px 0px 0px 1px ${props.theme.colors.gray_300}` : 'none'
    }};
  }
`

export const StatIconChip = styled.div<{ $tone?: Tone }>`
  width: 40px;
  height: 40px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  background-color: ${props => {
    switch (props.$tone) {
      case 'success': return props.theme.colors.successLight
      case 'danger': return props.theme.colors.dangerLight
      case 'warning': return props.theme.colors.warningLight
      default: return props.theme.colors.primaryLight
    }
  }};
`

export const StatValue = styled.p<{ $tone?: Tone }>`
  font-weight: 700;
  font-size: 22px;
  margin: 0px;
  color: ${props => (props.$tone === 'danger' ? props.theme.colors.dangerActive : props.theme.colors.gray_900)};
`

export const StatLabel = styled.p`
  font-weight: 600;
  font-size: 12px;
  color: ${props => props.theme.colors.gray_600};
  margin: 2px 0px 0px 0px;
`

// A simple two-column key/value block for the catch-all "advanced" section.
export const KeyValueRow = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 16px;
  padding: 6px 0px;
  border-bottom: 1px solid ${props => props.theme.colors.gray_100};
  font-size: 13px;
  overflow-wrap: anywhere;
  min-width: 0;
  max-width: 720px;
`

// ---- Notifications / statistics tab ----
export const GroupLabel = styled.p`
  font-weight: 700;
  font-size: 13px;
  color: ${props => props.theme.colors.gray_900};
  margin: 18px 0px 4px 0px;
`

export const ToggleRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 10px 0px;
  border-bottom: 1px solid ${props => props.theme.colors.gray_100};
`

export const ToggleLabel = styled.span`
  font-weight: 600;
  font-size: 14px;
  color: ${props => props.theme.colors.gray_900};
`

// One aligned list: label on the left, input and unit on the right, the unit in a fixed-width column
// so every input lines up. The two-column grid it replaces switched on the viewport rather than its
// own width, so inside a half-width card or the profile modal each column was ~270px and the rows
// wrapped unevenly - some inline, some with the input dropped to a second line (VLT-2637).
export const CalcGrid = styled.div`
  display: flex;
  flex-direction: column;
  gap: 14px;
`

export const CalcRow = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  align-items: center;
  gap: 8px 16px;

  /* A long label wraps inside its own column instead of pushing the input down. A single word that
     still does not fit breaks, so it can never run into the input. */
  > *:first-child {
    overflow-wrap: anywhere;
  }

  /* Too narrow for both on one line: the label takes the width and the input sits under it. */
  @media (max-width: ${BREAKPOINTS.phone}px) {
    grid-template-columns: minmax(0, 1fr);
  }
`

export const CalcInput = styled.input`
  width: 110px;
  @media (max-width: ${BREAKPOINTS.shell}px) { min-height: ${TAP_MIN}px; }
  padding: 8px 10px;
  border-radius: 8px;
  border: 1px solid ${props => props.theme.colors.gray_300};
  background-color: transparent;
  color: ${props => props.theme.colors.gray_900};
  font-size: 16px;
  font-weight: 600;
  text-align: right;
  box-sizing: border-box;
`

export const CalcUnit = styled.span`
  font-size: 12px;
  color: ${props => props.theme.colors.gray_500};
  width: 84px;
  flex: 0 0 auto;
  white-space: nowrap;
`

export const SaveRow = styled.div`
  display: flex;
  gap: 12px;
  justify-content: flex-end;
  margin-top: 20px;
`

export const LangGrid = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
`

export const LangOption = styled.div<{ $selected: boolean }>`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 14px;
  border-radius: 10px;
  cursor: pointer;
  font-weight: 600;
  font-size: 14px;
  @media (max-width: ${BREAKPOINTS.shell}px) { min-height: ${TAP_MIN}px; }
  color: ${props => (props.$selected ? props.theme.colors.primaryActive : props.theme.colors.gray_700)};
  border: 1px solid ${props => (props.$selected ? props.theme.colors.primary : props.theme.colors.gray_300)};
  background-color: ${props => (props.$selected ? props.theme.colors.primaryLight : 'transparent')};
`

export const RecipientRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 0px;
`

export const RecipientInput = styled.input`
  flex: 1;
  padding: 8px 12px;
  border-radius: 8px;
  border: 1px solid ${props => props.theme.colors.gray_300};
  background-color: transparent;
  color: ${props => props.theme.colors.gray_900};
  font-size: 16px;
  box-sizing: border-box;
`

// ---- Toolbar (mirrors Chargers.tsx / Charges.tsx) ----
export const ToolbarContainer = styled.div`
  position: absolute;
  top: 0px;
  right: 12px;
  display: flex;
`

export const RefreshTimerLabel = styled.p`
  font-weight: 400;
  font-size: 10px;
  color: ${props => props.theme.colors.gray_600};
  margin-top: 4px;
  text-align: center;
`

// Transparent, keyboard-accessible button wrapper for clickable icons.
export const IconButton = styled.button`
  background: none;
  border: none;
  padding: 0;
  margin: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: inherit;

  @media (max-width: ${BREAKPOINTS.shell}px) {
    min-width: ${TAP_MIN}px;
    min-height: ${TAP_MIN}px;
  }
`

// ---- Device rows (Tab B) ----
export const DeviceRow = styled.div`
  padding: 10px 0px;
  border-bottom: 1px solid ${props => props.theme.colors.gray_100};
`

export const DeviceName = styled.p`
  font-weight: 700;
  font-size: 15px;
  color: ${props => props.theme.colors.gray_900};
  margin: 0px;
`

export const DeviceMeta = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 4px 20px;
  margin-top: 4px;
  font-size: 13px;
  color: ${props => props.theme.colors.gray_600};
`

export const AdvancedKey = styled.span`
  color: ${props => props.theme.colors.gray_500};
`

// Charger name / id cell that truncates with an ellipsis when it doesn't fit (table-layout is auto,
// so the width must be pixel-bounded for text-overflow to trigger).
export const ChargerNameTop = styled.div`
  max-width: 340px;
  font-weight: 700;
  font-size: 14px;
  color: ${props => props.theme.colors.gray_900};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`

export const ChargerNameSub = styled.div`
  max-width: 340px;
  font-size: 12px;
  color: ${props => props.theme.colors.gray_500};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`

// ---- Modal (aligns with the Chargers small-modal convention) ----
export const ModalPad = styled.div`
  padding: 24px;
  position: relative;
`

export const ModalCloseButtonContainer = styled.div`
  position: absolute;
  top: 26px;
  right: 26px;
`

export const ModalTitle = styled.p`
  font-weight: 700;
  font-size: 16px;
  color: ${props => props.theme.colors.gray_900};
  margin: 0px 0px 20px 0px;
`

export const ModalButtonRow = styled.div`
  display: flex;
  gap: 12px;
  margin-top: 20px;
`
